import React, { useState } from 'react'
import { useCalculateRescheduleImpact } from '../../hooks/useTasks'
import type { Task } from '../../hooks/useTasks'
import { APP_CONFIG } from '../../lib/config'

interface RescheduleImpactCalculatorProps {
  projectId: number
  task: Task
  onClose: () => void
  onApply?: (newStartDate: string, newEndDate: string) => void
}

export const RescheduleImpactCalculator: React.FC<RescheduleImpactCalculatorProps> = ({
  projectId,
  task,
  onClose,
  onApply
}) => {
  const [delayDays, setDelayDays] = useState(1)
  const [costPerDay, setCostPerDay] = useState(APP_CONFIG.DEFAULT_DAILY_COST)
  const [impact, setImpact] = useState<any>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  
  const { mutate: calculateImpact } = useCalculateRescheduleImpact(projectId)
  
  const handleCalculate = () => {
    setIsCalculating(true)
    calculateImpact(
      { taskId: task.id, delayDays, costPerDay },
      {
        onSuccess: (data) => {
          setImpact(data)
        },
        onSettled: () => setIsCalculating(false)
      }
    )
  }
  
  const handleApply = () => {
    if (impact) {
      onApply?.(impact.newStartDate, impact.newEndDate)
      onClose()
    }
  }
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Reschedule Impact Calculator</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Name</label>
              <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{task.taskName}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Schedule</label>
              <p className="text-sm text-gray-600">
                {new Date(task.startDate).toLocaleDateString()} - {new Date(task.endDate).toLocaleDateString()}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delay Days</label>
                <input
                  type="number"
                  min="1"
                  value={delayDays}
                  onChange={(e) => setDelayDays(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Day (IDR)</label>
                <input
                  type="number"
                  min="0"
                  value={costPerDay}
                  onChange={(e) => setCostPerDay(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <button
              onClick={handleCalculate}
              disabled={isCalculating}
              className="w-full px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCalculating ? 'Calculating...' : 'Calculate Impact'}
            </button>
            
            {impact && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-3">Impact Analysis</h3>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">New Start Date:</span>
                    <span className="font-medium">{new Date(impact.newStartDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">New End Date:</span>
                    <span className="font-medium">{new Date(impact.newEndDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Additional Operational Cost:</span>
                    <span className="font-medium text-red-600">Rp {impact.additionalOperationalCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Impacted Tasks:</span>
                    <span className="font-medium">{impact.impactedTaskIds.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Schedule Slippage:</span>
                    <span className="font-medium text-red-600">{impact.estimatedScheduleSlippage} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Cost Increase:</span>
                    <span className="font-medium text-red-600">Rp {impact.estimatedCostIncrease.toLocaleString()}</span>
                  </div>
                </div>
                
                {impact.impactedTaskIds.length > 0 && (
                  <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200 text-xs text-yellow-800">
                    ⚠️ This delay will impact {impact.impactedTaskIds.length} downstream task(s)
                  </div>
                )}
                
                <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Apply Reschedule
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

RescheduleImpactCalculator.displayName = 'RescheduleImpactCalculator'